import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Card,
  CardContent,
  Tab,
  Tabs,
  Chip,
  IconButton,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
} from '@mui/material';
import { Add, Delete, SmartToy, Mail } from '@mui/icons-material';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { adminAPI } from '../services/api';

const TabPanel = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
};

const AdminPanel = () => {
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [aiBudget, setAiBudget] = useState({ budget_limit: 0, current_spend: 0, remaining: 0 });
  const [userDialog, setUserDialog] = useState({ open: false, username: '', email: '', full_name: '', password: '', role: 'user', is_temporary_password: true });
  const [aiModels, setAiModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [loadingModels, setLoadingModels] = useState(false);
  const [destinations, setDestinations] = useState([]);
  const [senderAccounts, setSenderAccounts] = useState([]);
  const [oauthConfig, setOAuthConfig] = useState({ configured: false, auth_url: '' });
  const [loadingOAuth, setLoadingOAuth] = useState(false);
  const [destinationDialog, setDestinationDialog] = useState({
    open: false,
    name: '',
    slug: '',
    email: '',
    portal_url: '',
    description: '',
    legal_citation: '',
    agency_address: '',
    is_nextrequest: false,
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
    fetchAIBudget();
    fetchAIModels();
    fetchDestinations();
    fetchSenderAccounts();
    fetchOAuthConfig();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchAIBudget = async () => {
    try {
      const response = await adminAPI.getAIBudget();
      setAiBudget(response.data);
    } catch (error) {
      console.error('Error fetching AI budget:', error);
    }
  };

  const fetchAIModels = async () => {
    setLoadingModels(true);
    try {
      const response = await adminAPI.getAIModels();
      setAiModels(response.data.models);
      // Set current model from the list
      const geminiProLatest = response.data.models.find(m => m.name.includes('gemini-pro-latest'));
      if (geminiProLatest) {
        setSelectedModel(geminiProLatest.name);
      }
    } catch (error) {
      console.error('Error fetching AI models:', error);
    } finally {
      setLoadingModels(false);
    }
  };

  const handleUpdateAIModel = async () => {
    try {
      await adminAPI.updateAIModel(selectedModel);
      alert(`AI model updated to ${selectedModel}. Please restart the container for changes to take effect.`);
    } catch (error) {
      console.error('Error updating AI model:', error);
      alert('Failed to update AI model. Please try again.');
    }
  };

  const handleCreateUser = async () => {
    try {
      await adminAPI.createUser({
        username: userDialog.username,
        email: userDialog.email,
        full_name: userDialog.full_name || null,
        password: userDialog.password,
        role: userDialog.role,
        is_temporary_password: userDialog.is_temporary_password,
      });
      setUserDialog({ open: false, username: '', email: '', full_name: '', password: '', role: 'user', is_temporary_password: true });
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Failed to create user: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await adminAPI.deleteUser(userId);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const fetchDestinations = async () => {
    try {
      const response = await adminAPI.getDestinations();
      setDestinations(response.data);
    } catch (error) {
      console.error('Error fetching destinations:', error);
    }
  };

  const fetchSenderAccounts = async () => {
    try {
      const response = await adminAPI.getSenderAccounts();
      setSenderAccounts(response.data);
    } catch (error) {
      console.error('Error fetching sender accounts:', error);
    }
  };

  const handleDeleteDestination = async (id) => {
    if (!window.confirm('Are you sure you want to delete this destination?')) return;
    try {
      await adminAPI.deleteDestination(id);
      fetchDestinations();
    } catch (error) {
      console.error('Error deleting destination:', error);
      alert('Failed to delete destination');
    }
  };

  const handleCreateDestination = async () => {
    try {
      await adminAPI.createDestination(destinationDialog);
      setDestinationDialog({
        open: false,
        name: '',
        slug: '',
        email: '',
        portal_url: '',
        description: '',
        legal_citation: '',
        agency_address: '',
        is_nextrequest: false,
        is_active: true
      });
      fetchDestinations();
    } catch (error) {
      console.error('Error creating destination:', error);
      alert('Failed to create destination: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeleteSenderAccount = async (id) => {
    if (!window.confirm('Are you sure you want to delete this sender account?')) return;
    try {
      await adminAPI.deleteSenderAccount(id);
      fetchSenderAccounts();
    } catch (error) {
      console.error('Error deleting sender account:', error);
      alert('Failed to delete sender account');
    }
  };

  const fetchOAuthConfig = async () => {
    try {
      const response = await adminAPI.getGmailOAuthConfig();
      setOAuthConfig(response.data);
    } catch (error) {
      console.error('Error fetching OAuth config:', error);
    }
  };

  const handleConnectGmail = () => {
    if (oauthConfig.auth_url) {
      // Redirect to Google OAuth
      window.location.href = oauthConfig.auth_url;
    } else {
      alert('OAuth is not configured. Please contact administrator.');
    }
  };

  return (
    <Container maxWidth="lg">
      <Typography variant="h4" gutterBottom>
        Admin Panel
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Users" />
          <Tab label="Destinations" />
          <Tab label="Email Accounts" />
          <Tab label="AI Budget" />
          <Tab label="AI Models" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">User Management</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setUserDialog({ ...userDialog, open: true })}
          >
            Add User
          </Button>
        </Box>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.full_name || '—'}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip label={user.role} color={user.role === 'admin' ? 'primary' : 'default'} size="small" />
                    </TableCell>
                    <TableCell>
                      {user.is_temporary_password ? (
                        <Chip label="Temp Password" color="warning" size="small" />
                      ) : user.is_active ? (
                        <Chip label="Active" color="success" size="small" />
                      ) : (
                        <Chip label="Inactive" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteUser(user.id)}
                        title="Delete user"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">PRA Destinations</Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setDestinationDialog({ ...destinationDialog, open: true })}
          >
            Add Destination
          </Button>
        </Box>

        <Alert severity="warning" sx={{ mb: 3 }}>
          Destinations are agencies that receive PRA requests. Be careful when editing - changes affect all users.
        </Alert>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {destinations.map((dest) => (
                  <TableRow key={dest.id}>
                    <TableCell>{dest.id}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {dest.name}
                      </Typography>
                      {dest.description && (
                        <Typography variant="caption" color="text.secondary">
                          {dest.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <code>{dest.slug}</code>
                    </TableCell>
                    <TableCell>
                      {dest.email ? (
                        <a href={`mailto:${dest.email}`}>{dest.email}</a>
                      ) : dest.portal_url ? (
                        <a href={dest.portal_url} target="_blank" rel="noopener noreferrer">
                          Web Portal
                        </a>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {dest.is_nextrequest ? (
                        <Chip label="NextRequest" color="info" size="small" />
                      ) : (
                        <Chip label="Email" color="default" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={dest.is_active ? 'Active' : 'Inactive'}
                        color={dest.is_active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        onClick={() => handleDeleteDestination(dest.id)}
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Dialog open={destinationDialog.open} onClose={() => setDestinationDialog({ ...destinationDialog, open: false })} maxWidth="md" fullWidth>
          <DialogTitle>Add New Destination</DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Name"
                  value={destinationDialog.name}
                  onChange={(e) => setDestinationDialog({ ...destinationDialog, name: e.target.value })}
                  helperText="Display name (e.g., 'UC Berkeley')"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Slug"
                  value={destinationDialog.slug}
                  onChange={(e) => setDestinationDialog({ ...destinationDialog, slug: e.target.value })}
                  helperText="URL-friendly identifier (e.g., 'uc-berkeley')"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  value={destinationDialog.email}
                  onChange={(e) => setDestinationDialog({ ...destinationDialog, email: e.target.value })}
                  helperText="Official PRA email address"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Portal URL"
                  value={destinationDialog.portal_url}
                  onChange={(e) => setDestinationDialog({ ...destinationDialog, portal_url: e.target.value })}
                  helperText="NextRequest or web portal URL (if applicable)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  label="Description"
                  value={destinationDialog.description}
                  onChange={(e) => setDestinationDialog({ ...destinationDialog, description: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Legal Citation"
                  value={destinationDialog.legal_citation}
                  onChange={(e) => setDestinationDialog({ ...destinationDialog, legal_citation: e.target.value })}
                  helperText="CPRA citation"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Agency Address"
                  value={destinationDialog.agency_address}
                  onChange={(e) => setDestinationDialog({ ...destinationDialog, agency_address: e.target.value })}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDestinationDialog({ ...destinationDialog, open: false })}>Cancel</Button>
            <Button onClick={handleCreateDestination} variant="contained">Create</Button>
          </DialogActions>
        </Dialog>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Email Accounts</Typography>
          {oauthConfig.configured && (
            <Button
              variant="contained"
              startIcon={<Mail />}
              onClick={handleConnectGmail}
              color="success"
            >
              Connect Gmail Account
            </Button>
          )}
        </Box>

        {!oauthConfig.configured ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              <strong>Gmail OAuth Not Configured</strong>
            </Typography>
            <Typography variant="body2">
              To add Gmail sender accounts, you need to set up Google OAuth credentials:
              <br /><br />
              1. Go to <a href="https://console.cloud.google.com/" target="_blank" rel="noopener">Google Cloud Console</a>
              <br />
              2. Create a new project or select existing
              <br />
              3. Enable Gmail API
              <br />
              4. Create OAuth 2.0 credentials (Web application)
              <br />
              5. Add redirect URI: <code>http://localhost:8000/api/admin/gmail/oauth/callback</code>
              <br />
              6. Set environment variables:
              <br />
              <code>GMAIL_CLIENT_ID=your_client_id</code>
              <br />
              <code>GMAIL_CLIENT_SECRET=your_client_secret</code>
              <br /><br />
              Then restart the Docker container.
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            Click "Connect Gmail Account" to add a Gmail sender account via OAuth.
            You'll be redirected to Google to authorize the PRA Machine to send emails from your account.
          </Alert>
        )}

        {senderAccounts.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Mail sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No Sender Accounts Configured
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              {oauthConfig.configured
                ? "Connect a Gmail account to enable email sending"
                : "Configure Gmail OAuth to connect accounts"}
            </Typography>
          </Paper>
        ) : (
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Email Address</TableCell>
                    <TableCell>Account Name</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Token Expiry</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {senderAccounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell>{account.id}</TableCell>
                      <TableCell>
                        <a href={`mailto:${account.email_address}`}>{account.email_address}</a>
                      </TableCell>
                      <TableCell>{account.account_name || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={account.is_active ? 'Active' : 'Inactive'}
                          color={account.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {account.token_expiry
                          ? new Date(account.token_expiry).toLocaleDateString()
                          : 'Never'}
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteSenderAccount(account.id)}
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Typography variant="h6" gutterBottom>
          AI Budget Status
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Budget Limit
                </Typography>
                <Typography variant="h4">${aiBudget.budget_limit.toFixed(2)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Current Spend
                </Typography>
                <Typography variant="h4" color="primary">
                  ${aiBudget.current_spend.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Remaining
                </Typography>
                <Typography variant="h4" color="success">
                  ${aiBudget.remaining.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        <Typography variant="h6" gutterBottom>
          AI Model Selection
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          Select the AI model to use for generating PRA requests. Changes require a container restart to take effect.
        </Alert>

        {loadingModels ? (
          <Box display="flex" justifyContent="center" my={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Current Model
                  </Typography>
                  <FormControl fullWidth sx={{ mt: 2 }}>
                    <InputLabel id="model-select-label">Select AI Model</InputLabel>
                    <Select
                      labelId="model-select-label"
                      value={selectedModel}
                      onChange={(e) => setSelectedModel(e.target.value)}
                      label="Select AI Model"
                    >
                      {aiModels.map((model) => (
                        <MenuItem key={model.name} value={model.name}>
                          {model.display_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    startIcon={<SmartToy />}
                    onClick={handleUpdateAIModel}
                    sx={{ mt: 2 }}
                    fullWidth
                  >
                    Update Model
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Model Info
                  </Typography>
                  {selectedModel && aiModels.find(m => m.name === selectedModel) && (
                    <>
                      <Typography variant="h6" sx={{ mt: 2 }}>
                        {aiModels.find(m => m.name === selectedModel).display_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {aiModels.find(m => m.name === selectedModel).description}
                      </Typography>
                    </>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Available Models
          </Typography>
          <Paper>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Model Name</TableCell>
                    <TableCell>Display Name</TableCell>
                    <TableCell>Description</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {aiModels.slice(0, 10).map((model) => (
                    <TableRow
                      key={model.name}
                      selected={model.name === selectedModel}
                      hover
                      onClick={() => setSelectedModel(model.name)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{model.name}</TableCell>
                      <TableCell>{model.display_name}</TableCell>
                      <TableCell>{model.description.substring(0, 100)}...</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          {aiModels.length > 10 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Showing 10 of {aiModels.length} available models
            </Typography>
          )}
        </Box>
      </TabPanel>

      {/* Create User Dialog */}
      <Dialog open={userDialog.open} onClose={() => setUserDialog({ ...userDialog, open: false })} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Username"
            fullWidth
            variant="outlined"
            value={userDialog.username}
            onChange={(e) => setUserDialog({ ...userDialog, username: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Full Name (optional)"
            fullWidth
            variant="outlined"
            value={userDialog.full_name}
            onChange={(e) => setUserDialog({ ...userDialog, full_name: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            variant="outlined"
            value={userDialog.email}
            onChange={(e) => setUserDialog({ ...userDialog, email: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Temporary Password"
            type="text"
            fullWidth
            variant="outlined"
            value={userDialog.password}
            onChange={(e) => setUserDialog({ ...userDialog, password: e.target.value })}
            helperText="The user will be prompted to change this on first login."
            sx={{ mb: 2 }}
          />
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel>Role</InputLabel>
            <Select
              value={userDialog.role}
              onChange={(e) => setUserDialog({ ...userDialog, role: e.target.value })}
              label="Role"
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={userDialog.is_temporary_password}
                onChange={(e) => setUserDialog({ ...userDialog, is_temporary_password: e.target.checked })}
                color="primary"
              />
            }
            label="Require password change on first login"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialog({ ...userDialog, open: false })}>Cancel</Button>
          <Button onClick={handleCreateUser} variant="contained">Create User</Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default AdminPanel;
